import { useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button, Spinner } from '@adop/ui';
import { CheckCircle2, Mail } from 'lucide-react';
import type { InviteAcceptResponse } from '@adop/shared';
import { postJson, errorMessage } from '../lib/api';
import { clearInviteToken, peekInviteToken } from '../lib/invite-token';
import { useAuth } from '../lib/auth-context';
import { Alert } from '../components/Alert';

export function AuthCallbackPage() {
  const { loading, user, me, error: authError, refresh, signOut, signInWithGoogle } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [error, setError] = useState<string | null>(null);
  const [accepting, setAccepting] = useState(false);
  const handled = useRef(false);

  const tokenFromUrl = searchParams.get('token');
  // Supabase/Google can bounce back with the provider error in the query or hash.
  const hashParams = new URLSearchParams(window.location.hash.replace(/^#/, ''));
  const providerError =
    searchParams.get('error_description') ??
    searchParams.get('error') ??
    hashParams.get('error_description') ??
    hashParams.get('error');

  useEffect(() => {
    if (loading) return;
    if (providerError) {
      setError(providerError);
      return;
    }
    if (!user) {
      navigate('/', { replace: true });
      return;
    }
    if (handled.current) return;
    if (!me) return;
    handled.current = true;

    const token = tokenFromUrl ?? peekInviteToken();
    if (token) {
      setAccepting(true);
      postJson<InviteAcceptResponse>('/api/platform/invites/accept', { token })
        .then(async () => {
          clearInviteToken();
          await refresh();
          navigate('/dashboard', { replace: true });
        })
        .catch((cause: unknown) => {
          setError(errorMessage(cause));
          setAccepting(false);
        });
      return;
    }

    if (me.profile) navigate('/dashboard', { replace: true });
  }, [loading, user, me, tokenFromUrl, providerError, navigate, refresh]);

  if (loading || accepting) {
    return (
      <Centered>
        <Spinner size="lg" />
        <p className="mt-3 text-sm text-zinc-500">Signing you in…</p>
      </Centered>
    );
  }

  if (authError) {
    return (
      <Centered>
        <Alert>{authError}</Alert>
        <Button className="mt-4" onClick={() => signInWithGoogle()}>
          Try signing in again
        </Button>
      </Centered>
    );
  }

  if (error) {
    return (
      <Centered>
        <Alert>{error}</Alert>
        <div className="mt-4 flex gap-2">
          <Button onClick={() => navigate('/dashboard', { replace: true })}>Go to dashboard</Button>
          <Button variant="outline" onClick={() => signOut()}>
            Sign out
          </Button>
        </div>
      </Centered>
    );
  }

  if (me && !me.profile && me.pending_invites.length > 0) {
    return (
      <Centered>
        <Mail className="h-8 w-8 text-zinc-400" />
        <h1 className="mt-3 text-lg font-semibold text-zinc-900">You have an invitation</h1>
        <ul className="mt-4 w-full space-y-2 text-left">
          {me.pending_invites.map((invite) => (
            <li key={invite.id} className="rounded-lg border border-zinc-200 bg-white p-3 text-sm">
              You&apos;ve been invited to <strong>{invite.restaurant_name}</strong> as{' '}
              <strong>{invite.role}</strong>.
            </li>
          ))}
        </ul>
        <p className="mt-4 text-sm text-zinc-500">
          Open the invitation link in your email to accept it, or check back after signing in.
        </p>
        <Button className="mt-4" variant="outline" onClick={() => signOut()}>
          Sign out
        </Button>
      </Centered>
    );
  }

  if (me && !me.profile) {
    return (
      <Centered>
        <CheckCircle2 className="h-8 w-8 text-emerald-500" />
        <h1 className="mt-3 text-lg font-semibold text-zinc-900">
          This email ({me.user.email}) is new
        </h1>
        <p className="mt-2 text-sm text-zinc-500">
          Do you want to register a restaurant and start taking orders?
        </p>
        <div className="mt-5 flex gap-2">
          <Button onClick={() => navigate('/register')}>Yes, register</Button>
          <Button variant="outline" onClick={() => signOut()}>
            No, sign out
          </Button>
        </div>
      </Centered>
    );
  }

  return (
    <Centered>
      <Spinner size="lg" />
    </Centered>
  );
}

function Centered({ children }: { children: ReactNode }) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-zinc-50 px-4">
      <div className="flex w-full max-w-md flex-col items-center rounded-xl border border-zinc-200 bg-white p-8 text-center shadow-sm">
        {children}
      </div>
    </div>
  );
}
